// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspRecordDetail.tsx
import React, { useMemo } from 'react';
import { Spin, Card, Row, Col, Typography, Result, message, Table, Descriptions, Tag, Divider, Space } from 'antd';
import { ArrowLeftOutlined, EditOutlined, AuditOutlined, CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { fetchSqlData } from '../../../services/api';
import { VwQaqcInspRecord, VwQaqcInspFormDetails } from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

interface CombinedChecklistItem extends VwQaqcInspFormDetails {
  status: boolean | null; // From form_field_values
  remarkText: string | null; // From form_field_remarks
}

const QaqcInspRecordDetail: React.FC = () => {
  const { recordId } = useParams<{ recordId: string }>();
  const navigate = useNavigate();
  const numericRecordId = recordId ? parseInt(recordId, 10) : undefined;

  // 1. Fetch the main inspection record
  const {
    data: inspectionRecord,
    isLoading: isLoadingRecord,
    isError: isErrorRecord,
    error: errorRecord,
  } = useQuery<VwQaqcInspRecord, Error>({
    queryKey: ['qaqcInspRecordDetail', numericRecordId],
    queryFn: async () => {
      if (!numericRecordId) throw new Error("No valid record ID provided");
      const results = await fetchSqlData('vw_qaqc_insp_records', 'record_id', numericRecordId);
      if (!results || results.length === 0) {
        throw new Error(`QA/QC Inspection Record with ID ${numericRecordId} not found.`);
      }
      return results[0]; // Expecting a single record
    },
    enabled: !!numericRecordId,
  });

  const formDesignId = inspectionRecord?.form_id;

  // 2. Fetch the checklist item definitions for the form design used in this record
  const {
    data: formChecklistItems,
    isLoading: isLoadingChecklist,
    isError: isErrorChecklist,
    error: errorChecklist,
  } = useQuery<VwQaqcInspFormDetails[], Error>({
    queryKey: ['qaqcFormChecklistItemsForDetail', formDesignId],
    queryFn: async () => {
      if (!formDesignId) return []; // Should not happen if inspectionRecord is loaded
      const results = await fetchSqlData('vw_qaqc_insp_formdetails', 'design_id', formDesignId);
      // Assuming vw_qaqc_insp_formdetails is ordered by field_id or a sequence number
      // If not, ensure it's sorted here to match the order of form_field_values/remarks
      return results.sort((a: VwQaqcInspFormDetails, b: VwQaqcInspFormDetails) => a.field_id - b.field_id); // Example sort
    },
    enabled: !!formDesignId, // Only run if formDesignId is available
  });

  // Combine checklist items with their statuses and remarks
  const combinedChecklistData: CombinedChecklistItem[] = useMemo(() => {
    if (!formChecklistItems || !inspectionRecord?.form_field_values) {
      return [];
    }
    return formChecklistItems.map((item, index) => ({
      ...item,
      status: inspectionRecord.form_field_values ? inspectionRecord.form_field_values[index] : null,
      remarkText: inspectionRecord.form_field_remarks ? inspectionRecord.form_field_remarks[index] : null,
    }));
  }, [formChecklistItems, inspectionRecord]);

  // --- Loading and Error Handling ---
  if (!numericRecordId) {
    return (
      <Result status="error" title="Invalid Record ID" subTitle="No valid numeric ID provided in the URL."
        extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/records')}>Go Back to List</Button>} />
    );
  }

  const isLoading = isLoadingRecord || (!!formDesignId && isLoadingChecklist);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading inspection details..." />
      </div>
    );
  }

  if (isErrorRecord || (!!formDesignId && isErrorChecklist)) {
    const specificError = isErrorRecord ? errorRecord : errorChecklist;
    const errorMsg = (specificError as Error)?.message || 'Failed to load inspection details.';
    message.error(errorMsg);
    return (
      <Result status="error" title="Error Loading Inspection Details" subTitle={errorMsg}
        extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/records')}>Go Back to List</Button>} />
    );
  }

  if (!inspectionRecord) {
    return (
      <Result status="warning" title="Inspection Record Not Found" subTitle={`Could not find details for QA/QC inspection record ID: ${numericRecordId}`}
        extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/records')}>Go Back to List</Button>} />
    );
  }

  const checklistColumns = [
    { title: '#', dataIndex: 'index', key: 'index', render: (_text: any, _record: any, index: number) => index + 1, width: 50 },
    { title: 'Check Item', dataIndex: 'check_item', key: 'check_item' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: boolean | null) => {
        if (status === true) return <Tag icon={<CheckCircleOutlined />} color="success">Pass</Tag>;
        if (status === false) return <Tag icon={<CloseCircleOutlined />} color="error">Fail</Tag>;
        return <Tag icon={<MinusCircleOutlined />} color="default">N/A</Tag>; // Or however N/A was stored if it was an option
      },
    },
    { title: 'Remarks', dataIndex: 'remarkText', key: 'remarkText', render: (text: string | null) => text || '-' },
  ];

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qaqc/inspections/records')} type="text" />
          <AuditOutlined />
          <Title level={4} style={{ margin: 0 }}>
            QA/QC Inspection Record: {inspectionRecord.form_design_name} ({inspectionRecord.form_design_num}) - ID: {inspectionRecord.record_id}
          </Title>
        </Space>
      }
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      }
    >
      <Descriptions bordered column={2} size="small" layout="horizontal">
        <Descriptions.Item label="Form Name">{inspectionRecord.form_design_name}</Descriptions.Item>
        <Descriptions.Item label="Form No.">{inspectionRecord.form_design_num}</Descriptions.Item>
        <Descriptions.Item label="System">{`${inspectionRecord.system_name} (${inspectionRecord.sys_id})`}</Descriptions.Item>
        <Descriptions.Item label="Inspection Date">{dayjs(inspectionRecord.insp_date).format('YYYY-MM-DD')}</Descriptions.Item>
        <Descriptions.Item label="Inspector">{inspectionRecord.inspector_full_name}</Descriptions.Item>
        <Descriptions.Item label="Location / Area">{inspectionRecord.insp_place || '-'}</Descriptions.Item>
        <Descriptions.Item label="Witness 1">{inspectionRecord.witness1_full_name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Witness 2">{inspectionRecord.witness2_full_name || '-'}</Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" style={{ marginTop: 24 }}>Checklist Items</Divider>
      {isLoadingChecklist && !formChecklistItems && <Spin tip="Loading checklist..." />}
      {!isLoadingChecklist && combinedChecklistData.length > 0 && (
        <Table
          columns={checklistColumns}
          dataSource={combinedChecklistData}
          rowKey="field_id" // Assuming field_id is unique within the form design
          pagination={false}
          size="small"
          bordered
        />
      )}
      {!isLoadingChecklist && combinedChecklistData.length === 0 && inspectionRecord.form_field_values && (
         <Text type="secondary">This inspection form design had no checklist items at the time of inspection, or values were not recorded.</Text>
      )}
       {!isLoadingChecklist && !inspectionRecord.form_field_values && (
         <Text type="secondary">No checklist item values were recorded for this inspection.</Text>
      )}

      <Divider orientation="left" style={{ marginTop: 24 }}>Summary</Divider>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Overall Result">
          <Tag color={inspectionRecord.form_result ? 'success' : 'error'} style={{ fontSize: '1em', padding: '4px 8px' }}>
            {inspectionRecord.form_result ? 'PASS' : 'FAIL'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Overall Notes">
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {inspectionRecord.form_notes || '-'}
          </pre>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default QaqcInspRecordDetail;